/**
 * 单卡片组件 - 根据类型渲染不同图表
 * 文档: src/pages/opinion/specs/003-ads-overview/components/MetricCard/index.md
 */
import React, { useMemo } from 'react';
import { Row, Col } from 'tea-component';
import CardCommon from '@op/components/CardCommon';
import useLanguage from '@op/hooks/useLanguage';
import PercentageItem from '@op/components/Snippets/PercentageItem';
import BubbleCommon from '@op/components/BubbleCommon';
import type { TipsMapType } from '@op/components/BubbleCommon';
import MetricPieChart from './MetricPieChart';
import MetricTrendChart from './MetricTrendChart';
import { DBO_V, DBO_TM } from '@op/utils/format';
import { getFilterDateQOQText } from '@op/utils/time';
import './index.scss';
import MultiRingRatioBubble, { IMultiRingRatioDataItem } from '@op/pages/report/VoiceReportingTextAnalysis/KeyMetricCard/MultiRingRatioBubble';

// 环比数据（由 useAdStatsData 转换后的格式）
interface IChangeData {
  value: number; // 占比（PercentageItem 使用）
  oldValue: number; // 对比周期原始值（RingRatioBubble 展示用）
  oldTime: string[]; // [old_start_date, old_end_date]
  newTime: string[]; // [start_time, end_time]
}

interface ITimelineItem {
  time: string;
  value: number;
  oldValue?: number;
}

interface IMetricCardData {
  mainValue: number;
  dod?: IChangeData;
  sdlw?: IChangeData;
  timeline?: ITimelineItem[];
  pieData?: any;
}

interface IMetricCardProps {
  title: string;
  type: 'all_creative' | 'all_material' | 'new_material';
  filter: any;
  data?: IMetricCardData;
  translationMap?: Record<string, string>;
  loading?: boolean;
  onClick?: () => void;
  hasDataDatetime?: string | null;
  isSupportAdCreative?: boolean;
  tipsMap?: TipsMapType;
}

const MetricCard: React.FC<IMetricCardProps> = ({
  title,
  type,
  filter,
  data,
  translationMap,
  loading,
  onClick,
  hasDataDatetime,
  isSupportAdCreative,
  tipsMap,
}) => {
  const { formatMessage } = useLanguage();
  const { date_type, end_time } = filter || {};

  // ==================== 环比文案（根据 date_type 动态变化） ====================
  const { text: beforeText } = filter
    ? getFilterDateQOQText(filter)
    : { text: 'DoD' };

  // 同比文案（根据 date_type 动态变化）
  const sameDayLastWeek = useMemo(() => {
    const map: Record<string, string> = {
      daily: 'SDLW', // 上周同期
      weekly: 'SWLM', // 上周同月
      monthly: 'SMLY', // 去年同月
      hourly: 'SHLD', // 昨日同期
    };
    return map[date_type] || 'SDLW';
  }, [date_type]);

  // ==================== 是否显示 SDLW（单日才显示） ====================
  const showSDLW = useMemo(() => {
    if (!filter) return false;
    const { isSingleDate, isSingleWeek, isSingleMonth } = getFilterDateQOQText(filter);
    return isSingleDate || isSingleWeek || isSingleMonth;
  }, [filter]);

  // ==================== 标题（带指标定义 tooltip） ====================
  const titleElement = useMemo(() => {
    const text = formatMessage(title);
    if (!tipsMap) return text;
    return (
      <BubbleCommon placement="top" tipsMap={tipsMap}>
        {text}
      </BubbleCommon>
    );
  }, [title, tipsMap, formatMessage]);

  const multiRatioData = useMemo(() => {
    const { dod, sdlw } = data || {};
    const multiRatio: IMultiRingRatioDataItem[] = [];
    if (dod) {
      multiRatio.push({
        title: formatMessage({ id: beforeText }),
        oldTime: dod.oldTime,
        newTime: dod.newTime,
        oldValue: dod.oldValue,
        newValue: data?.mainValue || 0,
        formatValue: (value: number | string) => DBO_V.formatThousand(Number(value)),
      });
    }

    if (sdlw && showSDLW) {
      multiRatio.push({
        title: formatMessage({ id: sameDayLastWeek }),
        oldTime: sdlw.oldTime,
        newTime: sdlw.newTime,
        oldValue: sdlw.oldValue,
        newValue: data?.mainValue || 0,
        formatValue: (value: number | string) => DBO_V.formatThousand(Number(value)),
      });
    }
    return multiRatio;
  }, [data]);

  // ==================== 渲染逻辑 ====================
  return (
    <CardCommon
      className="dbo-metric-card"
      loading={loading}
      loadingHeight={120}
      title={titleElement}
      handleTitleOnClick={onClick}
      hasTitleArrow={isSupportAdCreative}
      dropdownsFilterBeforeElement={
        end_time ? (
          <span className="dbo-metric-card__title-date">
            {DBO_TM.sFormat(end_time)}
          </span>
        ) : undefined
      }
    >
      <Row className="dbo-metric-card__content" gap={20}>
        {/* 左侧：主数字 + 环比信息 */}
        <Col span={8}>
          <div className="dbo-metric-card__info">
            <div className="dbo-metric-card__value">
              {DBO_V.formatThousand(data?.mainValue ?? 0)}
            </div>

            {/* 环比区域（每个环比项有独立的浮窗） */}
            <div className="dbo-metric-card__trends">
              {/* DoD/WoW/MoM 环比 - 带独立浮窗 */}
              <MultiRingRatioBubble
                data={multiRatioData}
              >
                {
                  data?.dod && (
                    <div className="dbo-metric-card__trend-item">
                      <span className="dbo-metric-card__trend-label">
                        {formatMessage({ id: beforeText })}
                      </span>
                      <PercentageItem percentage={data.dod.value} />
                    </div>
                  )
                }
                {
                  (data?.sdlw && showSDLW) && (
                    <div className="dbo-metric-card__trend-item">
                      <span className="dbo-metric-card__trend-label">
                        {formatMessage({ id: sameDayLastWeek })}
                      </span>
                      <PercentageItem percentage={data.sdlw.value} />
                    </div>
                  )
                }
              </MultiRingRatioBubble>
            </div>
          </div>
        </Col>

        {/* 右侧：图表区域 */}
        <Col span={16}>
          <div className="dbo-metric-card__chart">
            {/* 饼图 - 在投创意数专用 */}
            {type === 'all_creative' && (
              <MetricPieChart
                data={data?.pieData}
                translationMap={translationMap}
                height={90}
                loading={loading}
                title={title}
              />
            )}

            {/* 趋势图 - 在投素材数/新增素材数专用 */}
            {type !== 'all_creative' && (
              <MetricTrendChart
                data={data?.timeline}
                height={90}
                loading={loading}
              />
            )}
          </div>
        </Col>
      </Row>
    </CardCommon>
  );
};

export default React.memo(MetricCard);
